import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { usePendencias } from "@/contexts/pendencias-context";
import { getCurrentContract } from "@/services/clientAreaService";
import {
  AlertTriangle,
  ArrowRight,
  FileText,
  HelpCircle,
  History,
  Package,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface QuickAccessCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  hasAlert?: boolean;
}

export default function HomePage() {
  const { user } = useAuth();
  const { getSidebarAlerts } = usePendencias();
  const sidebarAlerts = getSidebarAlerts();

  const clientName = user?.cli_fantasia || "Cliente";
  const cliCodigo = user?.cli_codigo;

  const [activeContractId, setActiveContractId] = useState<
    string | number | null
  >(null);

  useEffect(() => {
    if (!cliCodigo) return;

    const fetchActiveContract = async () => {
      try {
        const contratoData = await getCurrentContract(cliCodigo);

        if (
          contratoData &&
          contratoData.length === 1 &&
          contratoData[0]?.cve_codigo
        ) {
          setActiveContractId(contratoData[0].cve_codigo);
        } else {
          setActiveContractId(null);
        }
      } catch (error) {
        console.error("Erro ao buscar contrato ativo:", error);
      }
    };

    fetchActiveContract();
  }, [cliCodigo]);

  const quickAccessCards: QuickAccessCard[] = [
    {
      title: "Transportes Ativos",
      description: "Acompanhe e rastreie seu transporte aqui",
      icon: <Package className="w-12 h-12" />,
      to: activeContractId
        ? `/cliente/detalhes-contrato/${activeContractId}`
        : "/cliente/resumo",
      hasAlert: !!sidebarAlerts["inicio"],
    },
    {
      title: "Meu Perfil",
      description: "Dados pessoais, Endereços e Veículos",
      icon: <User className="w-12 h-12" />,
      to: "/cliente/dados-pessoais",
      hasAlert: !!sidebarAlerts["dados-pessoais"],
    },
    {
      title: "Cotações em aberto",
      description: "Gerenciar ou acompanhar suas cotações",
      icon: <FileText className="w-12 h-12" />,
      to: "/cliente/cotacoes-abertas",
      hasAlert: !!sidebarAlerts["cotacoes-abertas"],
    },
    {
      title: "Histórico de transportes",
      description: "Ver histórico completo de transportes",
      icon: <History className="w-12 h-12" />,
      to: "/cliente/contratos",
    },
    {
      title: "Central de ajuda",
      description: "Explorar opções de autoatendimento e suporte",
      icon: <HelpCircle className="w-12 h-12" />,
      to: "/cliente/ajuda/perguntas-frequentes",
      hasAlert: !!sidebarAlerts["central-ajuda"],
    },
  ];

  return (
    <div className="py-6 sm:py-10 w-full max-w-7xl mx-auto px-4">
      <h1 className="text-2xl sm:text-3xl font-bold text-n-green mb-2">
        Sua conta
      </h1>
      <p className="text-n-text-neutral mb-8">
        Olá, {clientName}! Acesse rapidamente suas informações e serviços.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {quickAccessCards.map((card, index) => (
          <Link
            key={index}
            to={card.to}
            className="group relative bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-200 hover:border-n-green"
          >
            {card.hasAlert && (
              <div className="absolute top-4 right-4">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            )}

            <div className="flex flex-col h-full">
              <div className="mb-4 text-n-green group-hover:scale-105 transition-transform duration-200">
                {card.icon}
              </div>

              <h2 className="text-lg font-semibold text-n-text mb-2 group-hover:text-n-green transition-colors">
                {card.title}
              </h2>

              <p className="text-sm text-n-text-neutral flex-1">
                {card.description}
              </p>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-n-green-s-light to-n-green-h-light rounded-xl p-6">
          <h3 className="text-xl font-bold text-n-green mb-3">Nova cotação</h3>
          <p className="text-n-text-neutral mb-4">
            Precisa transportar um veículo? Faça uma cotação rápida e sem
            compromisso.
          </p>
          <Link to="/fazer-cotacao" target="_blank" rel="noopener noreferrer">
            <Button className="bg-n-bg-green text-n-bg-2 hover:bg-n-bg-green/80 hover:text-n-bg-2">
              Fazer cotação
              <ArrowRight />
            </Button>
          </Link>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-n-green mb-3">
            Precisa de ajuda?
          </h3>
          <p className="text-n-text-neutral mb-4">
            Nossa central de ajuda está sempre disponível para você.
          </p>
          <div className="space-y-2">
            <Link
              to="/cliente/ajuda/abrir-chamado"
              className=" text-n-green hover:underline font-medium flex items-center gap-1"
            >
              <ArrowRight /> Abrir chamado
            </Link>
            <Link
              to="/cliente/ajuda/perguntas-frequentes"
              className=" text-n-green hover:underline font-medium flex items-center gap-1"
            >
              <ArrowRight /> Perguntas frequentes
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
